import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react-use-analytics-api/react-use-analytics-api/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1>useData</h1>
    <h2 {...{
      "id": "description"
    }}>{`Description`}</h2>
    <p>{`This hook returns an `}<inlineCode parentName="p">{`execute`}</inlineCode>{` function that can be used to query the Google Analytics Core Reporting API for data and get back the results.`}</p>
    <p>{`You can use the raw data to build your own charts using a charting library of your choosing.`}</p>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { useData } from 'react-use-analytics-api';
`}</code></pre>
    <p>{`In your function component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const query = {
  metrics: 'ga:sessions',
  dimensions: 'ga:date',
  'start-date': '28daysAgo',
  'end-date': 'today',
  ids: viewId,
};
const execute = useData(
  gapi,
  query,
  response => console.log('Data query response:', response),
  response => console.error('Data query error:', response)
);
React.useEffect(() => {
  execute();
}, [execute]);
`}</code></pre>
    <p>{`Provide the hook with `}<a parentName="p" {...{
        "href": "/react-use-analytics-api/useAnalyticsApi"
      }}><inlineCode parentName="a">{`gapi`}</inlineCode></a>{`, your `}<inlineCode parentName="p">{`query`}</inlineCode>{`, and two callbacks: `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` and `}<inlineCode parentName="p">{`onError`}</inlineCode>{`. `}</p>
    <p>{`Call `}<inlineCode parentName="p">{`execute`}</inlineCode>{` to run the query.`}</p>
    <h2 {...{
      "id": "signature"
    }}>{`Signature`}</h2>
    <p><inlineCode parentName="p">{`useData = (gapi?: GoogleAnalyticsEmbedAPI, query: Query, onSuccess?: (response: any) => void, onError?: (response: any) => void): (() => void)`}</inlineCode></p>
    <h3 {...{
      "id": "arguments"
    }}>{`Arguments`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Argument`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`gapi`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`GoogleAnalyticsEmbedAPI`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`undefined`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<a parentName="td" {...{
              "href": "/react-use-analytics-api/useAnalyticsApi"
            }}>{`Google Analytics Embed API`}</a>{`. When `}<inlineCode parentName="td">{`undefined`}</inlineCode>{`, calling `}<inlineCode parentName="td">{`useData`}</inlineCode>{` does nothing.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`query`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "https://developers.google.com/analytics/devguides/reporting/core/v3/reference#q_summary"
            }}><inlineCode parentName="a">{`Query`}</inlineCode></a></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The data `}<a parentName="td" {...{
              "href": "https://developers.google.com/analytics/devguides/reporting/core/v3/reference#q_summary"
            }}>{`query`}</a>{` for the Google Analytics Core Reporting API.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`onSuccess`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`(response) => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fired when the query has successfully completed. The `}<inlineCode parentName="td">{`response`}</inlineCode>{` argument will contain the query results.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`onError`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`(response) => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fired when an error occurs during the query process. If you want to get the error message from the response object it will be at `}<inlineCode parentName="td">{`response.error.message`}</inlineCode>{`.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "return"
    }}>{`Return`}</h3>
    <p>{`The hook returns a function, `}<inlineCode parentName="p">{`execute`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`execute() => void
`}</code></pre>
    <p>{`When `}<inlineCode parentName="p">{`gapi`}</inlineCode>{` is not `}<inlineCode parentName="p">{`undefined`}</inlineCode>{`, this function executes the `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/core/v3/reference#q_summary"
      }}>{`query`}</a>{` to the Google Analytics Core Reporting API using `}<a parentName="p" {...{
        "href": "https://developers.google.com/analytics/devguides/reporting/embed/v1/component-reference#data"
      }}><inlineCode parentName="a">{`gapi.analytics.report.Data`}</inlineCode></a>{`.`}</p>
    <p>{`After calling `}<inlineCode parentName="p">{`execute`}</inlineCode>{`, either the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` or `}<inlineCode parentName="p">{`onError`}</inlineCode>{` callback will be called.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      